import type {KeyboardEvent, ReactNode} from 'react'
import type {LucideIcon} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {cn} from '@/lib/utils'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card'

interface CardSectionProps {
    icon?: LucideIcon
    title: string
    description?: string
    to?: string
    children?: ReactNode
    'data-testid'?: string
    'data-help-prefix'?: string
}

export function CardSection({
    icon: Icon,
    title,
    description,
    to,
    children,
    'data-testid': testId,
    'data-help-prefix': helpPrefix,
}: CardSectionProps) {
    const navigate = useNavigate()

    const handleClick = to ? () => navigate(to) : undefined
    const handleKeyDown = to ? (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(to) }
    } : undefined

    return (
        <Card
            data-testid={testId}
            data-help-prefix={helpPrefix}
            className={cn(to && 'cursor-pointer hover:bg-muted/50 transition-colors')}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role={to ? 'link' : undefined}
            tabIndex={to ? 0 : undefined}
        >
            <CardHeader>
                <CardTitle data-testid={testId ? `${testId}-title` : 'card-section-title'}>
                    {Icon && (
                        <span className="flex items-center gap-2">
                            <Icon className="h-5 w-5"/>
                            {title}
                        </span>
                    )}
                    {!Icon && title}
                </CardTitle>
                {description && <CardDescription data-testid="card-section-description">{description}</CardDescription>}
            </CardHeader>
            {children && (
                <CardContent>
                    {children}
                </CardContent>
            )}
        </Card>
    )
}
